import { useMemo } from 'react';
import * as QuestionnaireApiModels from '@typings/questionnaires-api-models';
import { useFormContext } from '@/context/FormContext';

type BaseSection =
  & Pick<QuestionnaireApiModels.SectionTemplate, 'id' | 'label' | 'rank'>
  & {
    isLoading: boolean;
    fields: Array<{
      assignedTo: string[];
    }>;
  };

type Props<S extends BaseSection> = {
  allSections: S[];
  selectedAssignee: string;
};

type Response<S extends BaseSection> = {
  currentSectionData: S | null;
  visibleSections: S[];
  sectionsForNavigation: Array<{
    id: string;
    isLoading: boolean;
    label: string;
    progress: null;
  }>;
};

const useFilteredSections = <S extends BaseSection>({
  allSections,
  selectedAssignee,
}: Props<S>): Response<S> => {
  const {
    currentSection,
    sectionVisibility,
  } = useFormContext();
  return useMemo(() => {
    const filteredSectionsNew = allSections.map(section => ({
      ...section,
      fields: section.fields.filter(field =>
        selectedAssignee === 'All'
          ? true
          : field.assignedTo.includes(selectedAssignee),
      ),
    }));

    const visibleSectionsNew = filteredSectionsNew
      .filter(section => sectionVisibility[section.id])
      .sort((a, b) => a.rank - b.rank);

    const sectionsForNavigationNew = visibleSectionsNew.map(section => ({
      id: section.id,
      isLoading: section.isLoading,
      label: section.label,
      progress: null,
    }));

    const currentSectionDataNew = filteredSectionsNew.find(section => section.id === currentSection);

    return {
      currentSectionData: currentSectionDataNew ?? null,
      sectionsForNavigation: sectionsForNavigationNew,
      visibleSections: visibleSectionsNew,
    };
  }, [allSections, selectedAssignee, sectionVisibility, currentSection]);

};

export default useFilteredSections;